import { adminFetch } from './dataProvider';
import type { ConferenceResponsesResponse } from './types';

/**
 * GET /v1/admin/conferences/{conferenceId}/responses — pivot data for the
 * Responses tab. Returns the conference's question set + one row per lead
 * (including skippers, with an empty `answers` dict).
 *
 * Auth: D1-gated on `company_conferences` server-side. `company_admin` is
 * token-scoped; `companyId` is an optional override.
 */
export function fetchConferenceResponses(
  conferenceId: string,
  companyId?: string,
): Promise<ConferenceResponsesResponse> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  return adminFetch<ConferenceResponsesResponse>(
    `/v1/admin/conferences/${conferenceId}/responses${qs}`,
  );
}

export interface UpdateResponseBody {
  freeText?: string | null;
  selectedOptionIds?: string[];
}

/**
 * PATCH /v1/admin/conferences/{conferenceId}/responses/{personId}/{questionId}
 * — edit a single answer cell. Like the GET, `company_admin` is token-scoped;
 * `companyId` is an optional override.
 */
export function updateConferenceResponse(
  conferenceId: string,
  personId: string,
  questionId: string,
  body: UpdateResponseBody,
  companyId?: string,
): Promise<void> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  return adminFetch<void>(
    `/v1/admin/conferences/${conferenceId}/responses/${personId}/${questionId}${qs}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

/**
 * DELETE /v1/admin/conferences/{conferenceId}/responses/{personId}/{questionId}
 * — delete a single answer cell. Same auth/scoping as the PATCH.
 */
export function deleteConferenceResponse(
  conferenceId: string,
  personId: string,
  questionId: string,
  companyId?: string,
): Promise<void> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  return adminFetch<void>(
    `/v1/admin/conferences/${conferenceId}/responses/${personId}/${questionId}${qs}`,
    { method: 'DELETE' },
  );
}
